import * as React from "react";
import { useWebSocket } from "@/lib/cjs-interop";
import { OpenChatMessage, SearchSourcesData } from "@/types/open-chat";
import { useAppDispatch } from "@/store/hooks";
import { openChatApi } from "@/api/open-chat";

interface Props {
  conversationId: string;
}

interface WebSocketOpenChatMessage extends OpenChatMessage {
  event: "message" | "end" | "searching" | "search_sources" | "content_update";
}

export const OpenChatWebSocketHandler: React.FC<Props> = ({ conversationId }) => {
  const { lastJsonMessage } = useWebSocket<WebSocketOpenChatMessage>(`/pf-mainweb/open-chat/ws/${conversationId}`, {
    share: false,
    skipAssert: true,
    shouldReconnect: event => event?.code !== 1000,
  });
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (lastJsonMessage === null) return;

    dispatch(
      openChatApi.util.updateQueryData("getMessages", conversationId, drafts => {
        if (lastJsonMessage.event === "searching") {
          const existing = drafts.find(d => d.id === lastJsonMessage.id);
          if (!existing) {
            drafts.push({
              id: lastJsonMessage.id,
              type: "ai",
              content: "",
              isSearching: true,
            });
          }
          return;
        }

        if (lastJsonMessage.event === "search_sources") {
          const record = drafts.find(d => d.id === lastJsonMessage.id);
          if (record) {
            try {
              record.searchSources = JSON.parse(lastJsonMessage.content) as SearchSourcesData;
            } catch (error) {
              console.error("[OpenChat] Failed to parse search_sources:", error);
            }
          }
          return;
        }

        if (lastJsonMessage.event === "content_update") {
          const record = drafts.find(d => d.id === lastJsonMessage.id);
          if (record) {
            record.content = lastJsonMessage.content;
          } else {
            console.warn("[OpenChat] content_update received for unknown message id:", lastJsonMessage.id);
          }
          return;
        }

        if (lastJsonMessage.event === "message" || lastJsonMessage.event === "end") {
          const record = drafts.find(d => d.id === lastJsonMessage.id);
          if (!record) {
            if (lastJsonMessage.event === "end") return;
            drafts.push(lastJsonMessage);
          } else {
            if (!record.completed) {
              record.content += lastJsonMessage.content;

              if (lastJsonMessage.event === "end") {
                record.isSearching = false;
                record.completed = true;
              }
            }
          }
        }
      }),
    );
  }, [conversationId, lastJsonMessage, dispatch]);

  return null;
};
